import { PATHS } from 'routes/constants';
import {
	TPgCycleState,
	TPgFxRounding,
	TPgPaymentChannel,
	TPgPaymentMethodType,
	TPgRunKind,
	TPgRunStatus,
	TPgServiceKind,
	TPgUkOverride,
} from 'services/api/paymentGateway/types';
import { TPgOption, TPgTab, TPgTabGroup } from './types';

/** URL segments of the section tabs: `/payment-gateway/<tab>`. */
export enum EPgTab {
	// Operations
	PAYMENTS = 'payments',
	CUSTOMERS = 'customers',
	ALERTS = 'alerts',
	MONITORING = 'monitoring',
	AUDIT = 'audit',
	SYSTEM = 'system',
	// Settings
	SERVICES = 'services',
	FEES = 'fees',
	DD_FX = 'dd-fx',
	DEBT_RULES = 'debt-rules',
	SCHEDULE = 'schedule',
	ACCOUNTS = 'accounts',
	CLIENT_ACCESS = 'client-access',
}

export const PG_TAB_LABELS: Record<EPgTab, string> = {
	[EPgTab.PAYMENTS]: 'Payments',
	[EPgTab.CUSTOMERS]: 'Customers',
	[EPgTab.ALERTS]: 'Alerts',
	[EPgTab.MONITORING]: 'Monitoring',
	[EPgTab.AUDIT]: 'Audit',
	[EPgTab.SYSTEM]: 'System',
	[EPgTab.SERVICES]: 'Services',
	[EPgTab.FEES]: 'Fees',
	[EPgTab.DD_FX]: 'Direct Debit FX',
	[EPgTab.DEBT_RULES]: 'Debt Transfer Rules',
	[EPgTab.SCHEDULE]: 'Schedule',
	[EPgTab.ACCOUNTS]: 'Legal Entities & Accounts',
	[EPgTab.CLIENT_ACCESS]: 'Access & Notifications',
};

export const PG_DEFAULT_TAB = EPgTab.PAYMENTS;

/** Name of the dropdown that holds the settings tabs (also a breadcrumb item). */
export const PG_SETTINGS_GROUP = 'Settings';

const tab = (value: EPgTab): TPgTab => ({ value, label: PG_TAB_LABELS[value] });

/** Tabs shown directly in the tab bar. */
export const PG_PLAIN_TABS: TPgTab[] = [
	tab(EPgTab.PAYMENTS),
	tab(EPgTab.CUSTOMERS),
	tab(EPgTab.ALERTS),
	tab(EPgTab.MONITORING),
	tab(EPgTab.AUDIT),
	tab(EPgTab.SYSTEM),
];

/** Settings collapsed into one dropdown. */
export const PG_TAB_GROUPS: TPgTabGroup[] = [
	{
		name: PG_SETTINGS_GROUP,
		items: [
			tab(EPgTab.SERVICES),
			tab(EPgTab.FEES),
			tab(EPgTab.DD_FX),
			tab(EPgTab.DEBT_RULES),
			tab(EPgTab.SCHEDULE),
			tab(EPgTab.ACCOUNTS),
			tab(EPgTab.CLIENT_ACCESS),
		],
	},
];

export const PG_ALL_TAB_VALUES: string[] = [
	...PG_PLAIN_TABS.map((t) => t.value),
	...PG_TAB_GROUPS.flatMap((group) => group.items.map((t) => t.value)),
];

export const PG_GROUPED_TAB_VALUES: string[] = PG_TAB_GROUPS.flatMap((group) =>
	group.items.map((t) => t.value),
);

export const pgTabPath = (value: string): string => `${PATHS.PAYMENT_GATEWAY_PAGE}/${value}`;

/** Section tabs as page-search entries (filtered by `isPathAllowed` like the nav). */
export const PAYMENT_GATEWAY_SEARCH_PAGES: { name: string; path: string; description: string }[] = [
	...PG_PLAIN_TABS.map((t) => ({
		name: t.label,
		path: pgTabPath(t.value),
		description: 'Payment Gateway',
	})),
	...PG_TAB_GROUPS.flatMap((group) =>
		group.items.map((t) => ({
			name: t.label,
			path: pgTabPath(t.value),
			description: `Payment Gateway · ${group.name}`,
		})),
	),
];

// =============================================================
// Dictionaries
// =============================================================

export const PG_METHOD_TYPES: TPgPaymentMethodType[] = [
	'card',
	'applepay',
	'googlepay',
	'bacs_direct_debit',
];

export const PG_METHOD_TYPE_LABELS: Record<TPgPaymentMethodType, string> = {
	card: 'Card',
	applepay: 'Apple Pay',
	googlepay: 'Google Pay',
	bacs_direct_debit: 'Direct Debit',
};

export const PG_SERVICE_KIND_LABELS: Record<TPgServiceKind, string> = {
	invoice: 'Invoice',
	prepayment: 'Prepayment',
};

export const PG_SERVICE_KIND_OPTIONS: TPgOption<TPgServiceKind>[] = [
	{ value: 'invoice', label: PG_SERVICE_KIND_LABELS.invoice },
	{ value: 'prepayment', label: PG_SERVICE_KIND_LABELS.prepayment },
];

export const PG_CHANNEL_LABELS: Record<TPgPaymentChannel, string> = {
	link: 'Payment page (link)',
	dashboard: 'Dashboard (Make a payment, after binding)',
	auto: 'Monthly cycle (auto)',
	admin: 'Admin charge',
};

export const PG_CHANNEL_OPTIONS: TPgOption<TPgPaymentChannel>[] = (
	Object.keys(PG_CHANNEL_LABELS) as TPgPaymentChannel[]
).map((value) => ({ value, label: PG_CHANNEL_LABELS[value] }));

export const PG_RUN_KIND_LABELS: Record<TPgRunKind, string> = {
	daily: 'Daily',
	manual: 'Manual',
};

export const PG_RUN_STATUS_LABELS: Record<TPgRunStatus, string> = {
	running: 'Running',
	completed: 'Completed',
	failed: 'Failed',
};

export const PG_CYCLE_STATE_LABELS: Record<TPgCycleState, string> = {
	pending: 'Pending',
	dd_pending: 'DD pending',
	card_retrying: 'Card retrying',
	succeeded: 'Succeeded',
	exhausted: 'Exhausted',
	dd_failed: 'DD failed',
	no_method: 'No payment method',
	skipped: 'Skipped',
};

export const PG_FX_ROUNDING_OPTIONS: TPgOption<TPgFxRounding>[] = [
	{ value: 'up', label: 'Round up to the penny' },
	{ value: 'nearest', label: 'Round to the nearest penny' },
];

export const PG_UK_OVERRIDE_LABELS: Record<TPgUkOverride, string> = {
	auto: 'Auto (registered country)',
	force_uk: 'Always UK',
	force_not_uk: 'Never UK',
};

/** EAS `invoice_list_info.service_type` values known today (decision 4). */
export const PG_KNOWN_SERVICE_TYPES = ['eas', 'swiss', 'gpsr', 'addons', 'eu_ddp'];

/** Codes of services and legal entities: immutable, `^[a-z0-9_]{2,40}$`. */
export const PG_CODE_REGEX = /^[a-z0-9_]{2,40}$/;
/** EAS service type after normalization (trim, lower case). */
export const PG_SERVICE_TYPE_REGEX = /^[a-z0-9_]{1,40}$/;

/** Fraction precision accepted by the gateway (`numeric(9,6)`). */
export const PG_FRACTION_DECIMALS = 6;
/** Upper bound of fee and FX margin percents (exclusive): 50 %. */
export const PG_MAX_PERCENT = 50;
/** Limits of `quoteTtlSeconds`. */
export const PG_MIN_QUOTE_TTL_SECONDS = 60;
export const PG_MAX_QUOTE_TTL_SECONDS = 3600;
